import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SelectInputField } from '@/components/form/SelectInput';
import { Button } from '@/components/shared/button/Button';
import { Loader } from '@/components/shared/Loader';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { useEssAvailabilityTypes } from '@/services/availability/useAvailabilityTypes';
import { useRequestLeave } from '@/services/availability/useRequestLeave';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { LeaveAllowancePeriod, useGetEmployeeLeaveAllowancesQuery } from '@/services/gql/graphql.generated';
import { useMyId } from '@/services/staff/useMyId';
import { strings } from '@/services/translation/strings';
import { FC, Suspense, useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MyLeaveDateRangeFields, MyLeaveFormValues } from './MyLeaveDateRangeFields';
import { MyLeavePreview } from './MyLeavePreview';

interface LeaveFormProps {}

export const MyLeaveForm: FC<LeaveFormProps> = () => {
  const userId = useMyId();

  const leaveTypes = useEssAvailabilityTypes(true);

  const [searchParams] = useSearchParams();

  const startDateParam = searchParams.get('startDate');

  const startDate = startDateParam ? new Date(`${startDateParam}T00:00:00`) : new Date();

  const methods = useForm<MyLeaveFormValues>({
    defaultValues: {
      availabilityType: '',
      start: startDate,
      end: startDate
    }
  });

  const { registerForConfirmation, unregisterForConfirmation } = useConfirm(state => ({
    registerForConfirmation: state.registerForConfirmation,
    unregisterForConfirmation: state.unregisterForConfirmation
  }));

  const {
    formState: { isDirty },
    watch
  } = methods;

  useEffect(() => {
    if (isDirty) {
      registerForConfirmation(strings.settings.unsaved);
    } else {
      unregisterForConfirmation();
    }
  }, [isDirty]);

  useEffect(() => () => unregisterForConfirmation(), []);

  const { requestLeave, fetching } = useRequestLeave();

  const onSubmit = async (values: MyLeaveFormValues) => {
    const result = await requestLeave(values.start, values.end, values.availabilityType);

    if (result) {
      navigate('change-requested');
    }
  };

  const navigate = useNavigate();
  const onCancel = () => navigate(-1);

  const availabilityType = watch('availabilityType');

  const start = useWatch({ control: methods.control, name: 'start' });
  const end = useWatch({ control: methods.control, name: 'end' });

  const previewKey = `${availabilityType}|${dateToApiFormat(start)}|${dateToApiFormat(end)}`;

  const [previewState, setPreviewState] = useState<{
    key: string;
    exceeded: boolean;
    hasOverlap: boolean;
  } | null>(null);

  const onPreviewLoaded = useCallback(
    (exceeded: boolean, hasOverlap: boolean) => {
      setPreviewState({ key: previewKey, exceeded, hasOverlap });
    },
    [previewKey]
  );

  const previewReady = !availabilityType || previewState?.key === previewKey;
  const exceeded = previewState?.key === previewKey && previewState.exceeded;
  const hasOverlap = previewState?.key === previewKey && previewState.hasOverlap;

  const [{ data: yearly }] = useGetEmployeeLeaveAllowancesQuery({
    variables: { employeeId: userId, period: LeaveAllowancePeriod.PerYear, periodStart: dateToApiFormat(start) }
  });

  const remainingByType = new Map((yearly?.employeeLeaveAllowances ?? []).map(a => [a.leaveType.id, a.remaining]));

  const options = leaveTypes.map(type => {
    const remaining = remainingByType.get(type.leaveTypeId);
    return {
      name: remaining === undefined ? type.name : `${type.name} (${strings.common.daysRemaining(remaining)})`,
      value: type.leaveTypeId
    };
  });

  return (
    <SubmissionErrorBoundary>
      <TsForm methods={methods} onSubmit={onSubmit}>
        <div className="flex flex-col space-y-5">
          <MyLeaveDateRangeFields />

          <SelectInputField
            id="availabilityType"
            name="availabilityType"
            label={strings.timeshift.teamMember.calendarDayView.editAvailability.availabilityLeaveType}
            options={options}
          />

          {availabilityType && (
            <Suspense fallback={<Loader className="h-10 w-10 mx-auto" />}>
              <MyLeavePreview availabilityType={availabilityType} start={start} end={end} onLoaded={onPreviewLoaded} />
            </Suspense>
          )}

          <div className="flex w-full items-center justify-end gap-5">
            <Button className="flex-1" onClick={onCancel}>
              {strings.common.cancel}
            </Button>
            <Button
              className="flex-1"
              primary
              type="submit"
              loading={fetching}
              disabled={!availabilityType || !previewReady || exceeded || hasOverlap}
            >
              {strings.common.request}
            </Button>
          </div>
        </div>
      </TsForm>
    </SubmissionErrorBoundary>
  );
};
